import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, ShoppingBag, ShieldCheck, Lock, Globe } from "lucide-react";
import { toast } from "sonner";
import { loadRazorpay } from "../lib/razorpay";
import { useCountry } from "../contexts/CountryContext";
import { COUNTRY_LABELS, postcodeLabel, stateLabel } from "../lib/countries";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fallback if the public catalog is unreachable. Backend is still the source
// of truth for the actual charge — see /orders/create-with-styles.
const FALLBACK_PRICE = 1399;

const resolveCollectionId = (styleId) => {
  const s = (styleId || "").toLowerCase();
  if (s.startsWith("ipl_") || s === "ipl") return "ipl";
  if (s.startsWith("ren") || s === "renaissance") return "renaissance";
  return "minimalist";
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { originalImage, selectedStyle, styles, petDescription, selectedSize: stateSize, couponCode: stateCoupon } =
    location.state || {};
  const selectedSize = (stateSize || "8x10").toLowerCase();
  const { isInternational, pricing: intlPricing, formatPrice: fmtCurrency, country: ctxCountry, convertUsdToInr, fxRate, formatInr } = useCountry();
  // Currency-aware price formatter. ₹ for India, $ (or whatever currency)
  // for international visitors.
  const formatPrice = (n) =>
    typeof n !== "number" || isNaN(n)
      ? "—"
      : isInternational
      ? fmtCurrency(n)
      : `₹${Math.round(n).toLocaleString("en-IN")}`;

  const [submitting, setSubmitting] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponApplying, setCouponApplying] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discount }
  // {minimalist: {price:1399, prices:{a4:1399, '8x10':...}}, ...}
  const [collectionPriceMap, setCollectionPriceMap] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/styles/catalog`)
      .then((res) => {
        if (cancelled) return;
        const map = {};
        (res.data?.collections || []).forEach((c) => {
          if (c?.id) {
            map[c.id] = { price: c.price, prices: c.prices || {} };
          }
        });
        setCollectionPriceMap(map);
      })
      .catch(() => {
        if (!cancelled) setCollectionPriceMap({});
      })
      .finally(() => {
        if (!cancelled) setPriceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-apply a promo coupon. Priority order (first match wins):
  //   1. Explicit coupon passed via location.state (from /style/{slug})
  //   2. Coupon stashed in sessionStorage by a /promo/* landing page
  //   3. Admin-configured global default coupon from /api/site-settings
  // Runs once on mount and clears the sessionStorage stash so it doesn't
  // re-apply on refresh after the user removes it.
  useEffect(() => {
    let cancelled = false;
    const tryApply = (stash, source) => {
      if (cancelled || !stash || appliedCoupon) return;
      axios
        .post(`${API}/coupons/validate`, { code: stash })
        .then((res) => {
          if (cancelled) return;
          const discount = res.data?.discount;
          if (discount && discount > 0) {
            setAppliedCoupon({ code: stash, discount });
            // Only surface a toast for explicit promo intents — the silent
            // admin-default coupon should feel like a natural discount on
            // the price summary, not a noisy notification.
            if (source !== "default") {
              toast.success(`Promo code ${stash} applied — ₹${discount} off`);
            }
          }
        })
        .catch(() => {
          /* silently ignore — code may have been disabled */
        });
    };

    let stash = stateCoupon || null;
    if (!stash) {
      try {
        stash = sessionStorage.getItem("tsp_promo_coupon");
      } catch {
        /* ignore */
      }
    }
    if (stash) {
      tryApply(stash, "explicit");
      try {
        sessionStorage.removeItem("tsp_promo_coupon");
      } catch {
        /* ignore */
      }
      return () => {
        cancelled = true;
      };
    }

    // Fallback: admin's global default checkout coupon (may be empty).
    axios
      .get(`${API}/site-settings`)
      .then((res) => {
        const code = (res.data?.default_coupon_code || "").trim();
        if (code) tryApply(code, "default");
      })
      .catch(() => {
        /* ignore — checkout still works without a default */
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const collectionId = useMemo(
    () => resolveCollectionId(selectedStyle?.style_id),
    [selectedStyle]
  );
  const retailPrice = useMemo(() => {
    // International visitors → tier-based USD `original_price` (strikethrough).
    if (isInternational && intlPricing?.products) {
      const intl = intlPricing.products[selectedSize];
      if (intl && intl.original_price != null) return intl.original_price;
    }
    const c = collectionPriceMap?.[collectionId];
    if (!c) return FALLBACK_PRICE;
    return (c.prices && c.prices[selectedSize]) || c.price || FALLBACK_PRICE;
  }, [collectionPriceMap, collectionId, selectedSize, isInternational, intlPricing]);
  const intlSalePrice = useMemo(() => {
    if (!isInternational || !intlPricing?.products) return null;
    const intl = intlPricing.products[selectedSize];
    return intl?.sale_price ?? null;
  }, [isInternational, intlPricing, selectedSize]);
  const totalAmount = isInternational
    ? intlSalePrice ?? retailPrice
    : Math.max(1, retailPrice - (appliedCoupon?.discount || 0));
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    pincode: "",
    country: COUNTRY_LABELS[ctxCountry] || "India",
  });

  // Keep the address form's country in sync with the country selector in
  // the header. Doesn't override what the user has manually typed beyond
  // the initial default.
  useEffect(() => {
    setFormData((prev) => {
      const expected = COUNTRY_LABELS[ctxCountry] || prev.country;
      // Only auto-update when the user hasn't picked a country that differs
      // from the previous global selection.
      if (!prev.country || prev.country === "India" || COUNTRY_LABELS[ctxCountry]) {
        return { ...prev, country: expected };
      }
      return prev;
    });
  }, [ctxCountry]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const applyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) {
      toast.error("Enter a coupon code");
      return;
    }
    try {
      setCouponApplying(true);
      const res = await axios.post(`${API}/coupons/validate`, { code });
      setAppliedCoupon({ code: res.data.code, discount: res.data.discount });
      toast.success(`Coupon applied: ₹${res.data.discount} off`);
    } catch (err) {
      setAppliedCoupon(null);
      toast.error(err.response?.data?.detail || "Invalid coupon code");
    } finally {
      setCouponApplying(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
  };

  const validateForm = () => {
    if (!formData.name.trim()) { toast.error("Please enter your name"); return false; }
    if (!formData.email.trim()) { toast.error("Please enter your email"); return false; }
    if (!formData.phone.trim() || formData.phone.length < 10) { toast.error("Please enter a valid phone number"); return false; }
    if (!formData.address1.trim()) { toast.error("Please enter your address"); return false; }
    if (!formData.city.trim()) { toast.error("Please enter your city"); return false; }
    if (!formData.state.trim()) { toast.error("Please enter your state"); return false; }
    if (!formData.pincode.trim()) { toast.error("Please enter your pincode"); return false; }
    return true;
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      // Step 1: Create order in our DB (status: PENDING).
      // Pass the public Object Storage URL of the printable poster — never base64.
      // For international orders, send `intl_context` so the backend converts
      // the USD price to INR using the live FX rate and charges via Razorpay.
      const orderData = {
        original_image_url: originalImage,
        selected_style: selectedStyle.style_id,
        selected_style_image_url: selectedStyle.image_url,
        selected_size: selectedSize,
        ai_styles: styles.map((s) => ({
          style_id: s.style_id,
          style_name: s.style_name,
          image_url: s.image_url,
        })),
        customer: formData,
        retail_price: retailPrice,
        coupon_code: isInternational ? null : (appliedCoupon?.code || null),
        intl_context: isInternational
          ? { currency: "USD", usd_amount: totalAmount }
          : null,
      };

      const orderResponse = await axios.post(
        `${API}/orders/create-with-styles`,
        orderData
      );

      const {
        order_id,
        reference_number,
        total_amount: serverTotal,
      } = orderResponse.data;

      // Server is the source of truth for the final INR amount — either the
      // discounted India price or the FX-converted intl price.
      const finalAmount = serverTotal || totalAmount;
      const finalAmountPaise = Math.round(finalAmount * 100);

      // Step 2: Create Razorpay order (always in INR — intl is converted)
      const rzpOrderResponse = await axios.post(`${API}/razorpay/create-order`, {
        amount: finalAmountPaise,
        currency: "INR",
        receipt: reference_number,
      });

      const { razorpay_order_id, key_id } = rzpOrderResponse.data;

      // Lazily load the Razorpay SDK only when the customer is actually
      // about to pay — keeps the upload/home/style pages lightweight.
      try {
        await loadRazorpay();
      } catch (sdkErr) {
        toast.error("Could not load payment SDK. Please retry.");
        setSubmitting(false);
        return;
      }

      // Step 3: Open Razorpay checkout
      const options = {
        key: key_id,
        amount: finalAmountPaise,
        currency: "INR",
        name: "theSnugglePrints",
        description: isInternational
          ? `Custom Pet Portrait (USD $${totalAmount.toFixed(2)} charged in INR)`
          : `Custom Pet Portrait, framed A4 artboard`,
        order_id: razorpay_order_id,
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone,
        },
        theme: {
          color: "#002FA7",
        },
        handler: async function (response) {
          try {
            // Step 4: Verify payment
            await axios.post(`${API}/razorpay/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_id: order_id,
            });

            toast.success("Payment successful!");

            navigate("/success", {
              state: {
                order: { order_id, reference_number, status: "order_placed" },
                selectedStyle: selectedStyle,
              },
            });
          } catch (verifyError) {
            console.error("Verification error:", verifyError);
            toast.error("Payment verification failed. Contact support.");
          }
        },
        modal: {
          ondismiss: function () {
            setSubmitting(false);
            toast.error("Payment cancelled");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        setSubmitting(false);
        toast.error(`Payment failed: ${response.error.description}`);
      });
      rzp.open();
    } catch (error) {
      console.error("Error:", error);
      toast.error(
        error.response?.data?.detail || "Failed to process order. Please try again."
      );
      setSubmitting(false);
    }
  };

  if (!selectedStyle) {
    navigate("/");
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/styles", { state: location.state })}
            data-testid="back-to-styles-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <h1 className="text-2xl font-black tracking-tight">theSnugglePrints</h1>
          <div className="w-20"></div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Order Summary */}
            <div>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-bold mb-8">
                Order Summary
              </h2>

              <div className="bg-card border border-border/60 p-8 mb-6">
                <Label className="label-uppercase mb-4 block">Selected Style</Label>
                <div className="mb-4 overflow-hidden flex items-center justify-center bg-white">
                  <img
                    src={selectedStyle.preview_url || selectedStyle.image_url}
                    alt={selectedStyle.style_name}
                    className="w-full h-auto object-contain"
                    data-testid="checkout-selected-style-image"
                  />
                </div>
                <h3 className="text-xl font-bold mb-2">{selectedStyle.style_name}</h3>
                <p className="text-sm text-muted-foreground mb-1">
                  Size: <span className="font-medium text-foreground" data-testid="checkout-selected-size">{selectedSize.toUpperCase().replace("X", " × ")}</span>
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  Custom pet portrait, framed artboard, delivered to your door
                </p>

                <div className="border-t border-border pt-4 mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Framed Portrait</span>
                    <span className="text-sm">
                      {priceLoading ? (
                        <Loader2 className="h-3 w-3 animate-spin inline" />
                      ) : (
                        formatPrice(retailPrice)
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Shipping</span>
                    <span className="text-sm">FREE</span>
                  </div>

                  {/* Coupon row — Indian flow only. International gets the
                      Inaugural Offer baked into the price, no code needed. */}
                  {isInternational ? (
                    <div className="mt-3 mb-2 bg-[#FAF6EF] border border-[#EAE5DF] px-3 py-2 text-xs text-[#1A1A1A]">
                      Inaugural Offer applied — you&apos;re saving{" "}
                      <strong>{formatPrice(retailPrice - totalAmount)}</strong>.
                    </div>
                  ) : !appliedCoupon ? (
                    <div className="mt-4 mb-2">
                      <Label htmlFor="coupon-code" className="label-uppercase mb-2 block">
                        Have a coupon?
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          id="coupon-code"
                          type="text"
                          placeholder="Enter code"
                          value={couponCode}
                          onChange={(e) =>
                            setCouponCode(e.target.value.toUpperCase())
                          }
                          className="uppercase"
                          data-testid="coupon-input"
                          disabled={couponApplying || submitting}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={applyCoupon}
                          disabled={couponApplying || submitting}
                          data-testid="coupon-apply-button"
                        >
                          {couponApplying ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            "Apply"
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center mb-2 mt-2">
                      <span className="text-sm font-medium text-green-700">
                        Coupon{" "}
                        <code className="bg-green-50 border border-green-200 px-1.5 py-0.5 rounded text-xs">
                          {appliedCoupon.code}
                        </code>
                      </span>
                      <span className="text-sm flex items-center gap-2">
                        <span className="text-green-700 font-medium">
                          −{formatPrice(appliedCoupon.discount)}
                        </span>
                        <button
                          type="button"
                          onClick={removeCoupon}
                          className="text-xs text-muted-foreground hover:text-foreground underline"
                          data-testid="coupon-remove-button"
                        >
                          remove
                        </button>
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-4 border-t border-border">
                    <span className="text-lg font-bold">Total</span>
                    <span
                      className="text-2xl font-black text-primary"
                      data-testid="checkout-total"
                    >
                      {formatPrice(totalAmount)}
                    </span>
                  </div>

                  {/* INR conversion disclosure — intl visitors pay in INR via
                      Razorpay until PayPal goes live. */}
                  {isInternational && (
                    <div
                      className="mt-4 bg-[#FAF6EF] border border-[#EAE5DF] px-3 py-3 text-xs text-[#1A1A1A] leading-relaxed"
                      data-testid="intl-inr-disclosure"
                    >
                      <div className="flex items-start gap-2">
                        <Globe className="w-3.5 h-3.5 mt-[2px] shrink-0 text-[#6B6B6B]" />
                        <div>
                          Pricing shown in USD (${Number(totalAmount).toFixed(2)}).
                          You&apos;ll be charged{" "}
                          <strong data-testid="intl-inr-amount">
                            {convertUsdToInr(totalAmount) != null
                              ? formatInr(convertUsdToInr(totalAmount))
                              : "…"}
                          </strong>{" "}
                          via Razorpay at today&apos;s conversion rate
                          {fxRate ? ` (1 USD ≈ ₹${Number(fxRate).toFixed(2)})` : ""}.
                          Your card may apply a small foreign-transaction fee.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Shipping Form */}
            <div>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-bold mb-8">
                Shipping Details
              </h2>

              <div className="bg-card border border-border/60 p-8">
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="label-uppercase mb-2 block">Full Name</Label>
                    <Input id="name" name="name" value={formData.name} onChange={handleInputChange} placeholder="John Doe" data-testid="address-input-name" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email" className="label-uppercase mb-2 block">Email</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="john@example.com" data-testid="address-input-email" />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="label-uppercase mb-2 block">Phone</Label>
                      <Input id="phone" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="9876543210" data-testid="address-input-phone" />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address1" className="label-uppercase mb-2 block">Street Address</Label>
                    <Input id="address1" name="address1" value={formData.address1} onChange={handleInputChange} placeholder="123 Main Street" data-testid="address-input-street" />
                  </div>

                  <div>
                    <Label htmlFor="address2" className="label-uppercase mb-2 block">Apartment, Suite (Optional)</Label>
                    <Input id="address2" name="address2" value={formData.address2} onChange={handleInputChange} placeholder="Apt 4B" data-testid="address-input-apartment" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city" className="label-uppercase mb-2 block">City</Label>
                      <Input id="city" name="city" value={formData.city} onChange={handleInputChange} placeholder={isInternational ? "City" : "Mumbai"} data-testid="address-input-city" />
                    </div>
                    <div>
                      <Label htmlFor="state" className="label-uppercase mb-2 block">{stateLabel(ctxCountry)}</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder={isInternational ? stateLabel(ctxCountry) : "Maharashtra"}
                        data-testid="address-input-state"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pincode" className="label-uppercase mb-2 block">{postcodeLabel(ctxCountry)}</Label>
                      <Input
                        id="pincode"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleInputChange}
                        placeholder={isInternational ? postcodeLabel(ctxCountry) : "400001"}
                        data-testid="address-input-pincode"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country" className="label-uppercase mb-2 block">Country</Label>
                      {isInternational ? (
                        <select
                          id="country"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                          data-testid="address-input-country"
                        >
                          {Object.entries(COUNTRY_LABELS)
                            .filter(([code]) => code !== "IN")
                            .map(([code, name]) => (
                              <option key={code} value={name}>
                                {name}
                              </option>
                            ))}
                        </select>
                      ) : (
                        <Input
                          id="country"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          disabled
                          data-testid="address-input-country"
                        />
                      )}
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handlePlaceOrder}
                  disabled={submitting}
                  className="w-full h-14 text-base font-bold mt-8"
                  data-testid="place-order-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="mr-2 h-5 w-5" />
                      {isInternational
                        ? `Pay ${
                            convertUsdToInr(totalAmount) != null
                              ? formatInr(convertUsdToInr(totalAmount))
                              : "…"
                          } (${formatPrice(totalAmount)})`
                        : `Pay ${formatPrice(totalAmount)}`}
                    </>
                  )}
                </Button>

                {/* Trust Badges */}
                <div
                  className="mt-6 pt-6 border-t border-border/60 space-y-3"
                  data-testid="checkout-trust-badges"
                >
                  <div className="flex items-center justify-center gap-2 text-sm text-foreground/80">
                    <Lock className="h-4 w-4 text-green-700" />
                    <span className="font-medium">
                      Payments are secured by
                    </span>
                    <img
                      src="https://razorpay.com/assets/razorpay-logo.svg"
                      alt="Razorpay"
                      className="h-4 w-auto"
                      loading="lazy"
                      data-testid="razorpay-trust-logo"
                    />
                  </div>
                  <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    <ShieldCheck className="h-3.5 w-3.5 text-green-700" />
                    <span>100% Secure SSL Encrypted Checkout</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CheckoutPage;
